import axios from 'axios';
import {
  ASSIGN_USER_TO_PROJECT,
  ASSIGN_USER_TO_SUB_PROGRAM_URL,
  GET_COMPANIES,
  GET_USERS_ASSIGNED_TO_PROJECT,
  GET_USERS_ASSIGNED_TO_SUB_PROGRAM_URL,
  LIST_SUB_PROGRAMS_URL,
  ROLES_URL,
} from '@/utils/constants/constants';

function parse_response_data(response_data) {
  return typeof response_data === 'string'
    ? JSON.parse(response_data)
    : response_data;
}

function is_numeric_key_code(char_code) {
  return char_code <= 31 || (char_code >= 48 && char_code <= 57);
}

function build_sub_program_assignment_payload(sub_program_id, user_id, is_assigned) {
  const payload = {
    sub_program_id,
    user_id,
    is_assigned,
  };
  return payload;
}
function map_project_row(project) {
  const row = {
    id: project.id,
    name: project.name,
    description: project.project_description,
  };
  return row;
}

export default {
  fetch_roles() {
    return axios.get(ROLES_URL, {
      headers: this.headers,
      params: {
        project_id: this.project_id,
      },
    }).then((response) => {
      this.user_roles = response.data.data;
    }).catch((error) => {
      this.show_error(`Error fetching roles: ${error.message}`);
    });
  },

  get_projects() {
    this.project_list = [];
    return this.show_loader('Loading companies...').then((loader) => {
      return axios.get(GET_COMPANIES, { headers: this.headers }).then((response) => {
        const projects = parse_response_data(response.data)?.data || [];
        this.project_list = projects.map(map_project_row);
        this.project_headers = this.project_list.length ? Object.keys(this.project_list[0]) : [];
      }).catch((error) => {
        this.show_error(`Error getting companies: ${error}`);
      }).finally(() => {
        if (loader) this.close_loader(loader);
      });
    });
  },

  select_user(user_id) {
    this.assigned_projects = [];
    this.user_id = user_id;

    const payload = {
      user_id: this.user_id,
      page_size: 1000,
    };

    return axios.post(GET_USERS_ASSIGNED_TO_PROJECT, payload, { headers: this.headers })
      .then((response) => {
        const res = response.data;

        if (res.data && Array.isArray(res.data)) {
          this.assigned_projects = res.data.map(item => item.project);
        }
      })
      .catch((error) => {
        this.show_error(`Error fetching assigned projects: ${error.message}`);
      });
  },

  build_assignment_payload(project_id, force_assign = null) {
    const payload = {
      project_id,
      user_id: this.user_id,
      is_assigned: this.resolve_assignment_state(project_id, force_assign),
    };
    return payload;
  },

  resolve_assignment_state(project_id, force_assign = null) {
    if (force_assign !== null) return force_assign;
    return !this.assigned_projects.includes(project_id);
  },

  sync_assigned_projects(project_id, is_assigned) {
    if (is_assigned) {
      if (!this.assigned_projects.includes(project_id)) {
        this.assigned_projects.push(project_id);
      }
      return;
    }
    this.assigned_projects = this.assigned_projects.filter(id => id !== project_id);
  },

  async toggle_assignment(project_id, force_assign = null) {
    const payload = this.build_assignment_payload(project_id, force_assign);
    return axios.post(ASSIGN_USER_TO_PROJECT, payload, { headers: this.headers })
      .then(() => {
        this.sync_assigned_projects(project_id, payload.is_assigned);
      })
      .catch(() => {
        this.show_error('Error updating project assignment');
      });
  },

  set_assign_mode(mode) {
    this.assign_mode = mode;
    if (mode === 'sub_programs') {
      this.sp_filter_project_id = null;
      this.sp_list = [];
      this.assigned_sub_programs = [];
    }
  },

  reset_sub_program_assignments() {
    this.assigned_sub_programs = [];
  },

  async fetch_sub_program_list() {
    const response = await axios.get(LIST_SUB_PROGRAMS_URL, {
      headers: this.headers,
      params: { project_id: this.sp_filter_project_id },
    });
    this.sp_list = response.data.status === 'success' ? (response.data.data || []) : [];
  },

  async sync_single_sub_program_assignment(sp) {
    return axios.post(GET_USERS_ASSIGNED_TO_SUB_PROGRAM_URL, {
      sub_program_id: sp.id,
    }, { headers: this.headers }).then((res) => {
      const assigned = res.data.data;
      const is_assigned = Array.isArray(assigned) && assigned.some(a => a.assignee === this.user_id);
      if (!is_assigned) return;
      this.assigned_sub_programs.push(sp.id);
    }).catch(() => {
      // skip unavailable sub-program assignments
    });
  },

  async sync_sub_program_assignments() {
    this.reset_sub_program_assignments();
    for (const sp of this.sp_list) {
      await this.sync_single_sub_program_assignment(sp);
    }
  },

  async on_sp_project_filter_change() {
    if (!this.sp_filter_project_id) {
      this.clear_sub_program_state();
      return;
    }
    await this.fetch_sub_program_list().catch(() => {
      this.sp_list = [];
    });
    await this.sync_sub_program_assignments();
  },
  clear_sub_program_state() {
    this.sp_list = [];
    this.reset_sub_program_assignments();
  },
  build_sub_program_toggle_payload(sp_id) {
    const is_assigned = !this.assigned_sub_programs.includes(sp_id);
    return build_sub_program_assignment_payload(sp_id, this.user_id, is_assigned);
  },
  sync_toggled_sub_program_assignment(sp_id, is_assigned) {
    if (is_assigned) {
      this.assigned_sub_programs.push(sp_id);
      return;
    }
    this.assigned_sub_programs = this.assigned_sub_programs.filter(id => id !== sp_id);
  },
  async toggle_sub_program_assignment(sp_id) {
    const payload = this.build_sub_program_toggle_payload(sp_id);
    return axios.post(ASSIGN_USER_TO_SUB_PROGRAM_URL, payload, { headers: this.headers })
      .then(() => {
        this.sync_toggled_sub_program_assignment(sp_id, payload.is_assigned);
      })
      .catch(() => {
        this.show_error('Error updating sub-program assignment.');
      });
  },

  go_back_to_company_management() {
    this.$router.push('/company_management');
  },

  validate_numbers(event) {
    const char_code = event.which || event.keyCode;
    if (is_numeric_key_code(char_code)) return true;
    event.preventDefault();
    return false;
  },
};
